// Catalog.ID Client — The Wallets group, without a DOM
// ============================================================================
// Everything the Settings *Wallets* group and the Organization workspace's
// *Wallets* pane DECIDE, separated from everything they DRAW
// (registration-wallets §7, §4.8, §4.11, §4.12, §4.13). The rule this file
// exists to keep: a pane that can spend money must have its decisions
// unit-tested, and the client's vitest environment has no DOM, so every
// decision lives here and every pixel lives next door.
//
// Three postures the callers share and this module encodes:
//
//   READS NEVER SPEND.   {@link loadWalletView} lists; it never mints an
//                        address or a token. The FIRST receive address on a
//                        wallet is free and every later one burns a fee, so
//                        minting is {@link ensureReceiveAddress}, called from a
//                        member's click and from nowhere else.
//   THE LEDGER IS THE    Tokens are read back from the ledger on every open
//   TRUTH.               (§10), their codes and labels joined from the account's
//                        instruments blob (the v2 ledger returns neither, T2). The device cache of the default token is a
//                        cache: dropped on a revoke and on a reissue, BEFORE
//                        the new one is minted, so a revoked token can never be
//                        served to a module afterwards.
//   THE LIST IS A BLOB,  Since 2026-09-17 a personal wallet key is PIN-level
//   NOT A BAG ENTRY.     (D5 relaxed) and the account's wallets live in the
//                        `cid/bitcash-wallets` document, read and written by
//                        `state/bitcash-wallets-store.ts` and decided by
//                        `crypto/bitcash-wallets-doc.ts`. The bag half that
//                        used to live here — `readWalletSlots`,
//                        `nextWalletSlotIndex` and the three planners — moved
//                        there with it. Nothing in this module has ever seen a
//                        supersecret and nothing here does now.
//
// The publish half is deliberately a small exported function rather than an
// inline block: the print step's *Publish* checkbox (track D) and the Settings
// action are the same act, and one of them being a re-implementation of the
// other is how two dialogs start disagreeing about what was published.

import { verifyReceiveAddress } from '../crypto/bitcash';
import { walletDisplayLabel, type WalletSlot } from '../crypto/bitcash-wallets-doc';
import type { CreateAuthTokenInput, WorkerAuthToken } from '../key-grant/bitcash-worker-client';
import type { DefaultTokenCache } from '../key-grant/bitcash-token-cache';
import { encodeCachedToken } from '../key-grant/bitcash-token-cache';
import type { CreateClaimRequest, OperatorInfo, PublicClaimSummary } from '../api/types';

/** The shared default token (D6): unbound, Catalog.ID's service types (§9.3).
 *  Same string the key-grant family mints under; a second spelling here would
 *  make the Reissue action mint a token no module would ever adopt. */
export const DEFAULT_TOKEN_LABEL = 'catalog:default';

/** The optional public claim of §4.11. */
export const RECEIVE_ADDRESS_CLAIM_TYPE = 'bitcash_receive_address';

/** Per-delegate tokens on an organization wallet (§4.13). */
export function delegateTokenLabel(delegateCid: string): string {
    return `catalog:delegate:${delegateCid}`;
}

/** The organization wallet is an entitled org key for exactly these two roles
 *  (D14) — never admin, never viewer. */
const ORG_WALLET_ROLES = ['owner', 'treasurer'];

export function canUseOrgWallet(roles: readonly string[]): boolean {
    return roles.some(r => ORG_WALLET_ROLES.includes(r));
}

// ── the document half, re-exported ──────────────────────────────────────────
// `WalletSlot` and its default label moved to `crypto/bitcash-wallets-doc.ts`
// with the rest of the wallet document (cid-encrypted-storage §7.2). They are
// re-exported here because every consumer — both panes, the wallet card, the
// Key Grant picker — already imports them from this module, and the shape did
// not change: `keyIndex` is still the slot, deliberately, since the
// grant-policy id `bitcash_wallet#<slot>` is derived from it.

export { walletDisplayLabel };
export type { WalletSlot };

/**
 * What the Settings group says when the wallet list cannot be read.
 *
 * Here rather than inline in the pane because it is the one decision that
 * surface makes: the two NAMED reasons the blob layer raises — a locked tab and
 * an account with no derivation secret — each need their own sentence. Only the
 * first is one a member can act on. Anything else is reported verbatim; a wallet
 * list that fails silently is how somebody concludes their money is gone.
 */
export function walletsUnavailableMessage(err: unknown): string {
    const message = err instanceof Error ? err.message : String(err);
    if (message === 'cid-locked') {
        return 'Your wallets are in your encrypted storage and this Catalog.ID is locked. '
            + 'Unlock it and try again.';
    }
    // No remedy is offered because none exists here: sign-in only decrypts the
    // server's bag, and every registration mints the root, so an account that
    // lacks one was created outside the wizards.
    if (message === 'derive-root-missing' || message === 'derive-root-malformed') {
        return 'This account has no usable derivation secret, so its encrypted storage, which '
            + 'holds your wallets, cannot be opened. Every account gets one when it is registered, '
            + 'so this account was not set up completely, and signing in again will not repair it.';
    }
    return `Your wallets could not be read: ${message}`;
}

// ── the Worker half ─────────────────────────────────────────────────────────

/**
 * What these panes need from `<operator>.bit.cash`. Narrower than either
 * shipped client on purpose: this is the seam the tests replace, and a pane
 * that could reach the whole Worker API would be a pane whose behaviour no test
 * could bound. The live implementation lives in `bitcash-wallets-group.ts`.
 */
/**
 * A token as a pane shows it. The Worker's list carries running spend counters
 * (`daySpent` / `monthSpent` on its `AuthTokenState`) that the key-grant
 * client's normaliser does not carry across — it answers a grant, which has no
 * use for them. They are OPTIONAL here so the pane renders them the day that
 * client grows the two fields (§4.10's "merge later"), and says nothing about
 * spend until then rather than showing a confident zero.
 */
export interface WalletToken extends WorkerAuthToken {
    /** Bits debited through this token in the current CET day. */
    daySpent?: number;
    /** Bits debited through this token in the current CET month. */
    monthSpent?: number;
}

export interface WalletsWorker {
    listAuthTokens(walletKey: string): Promise<WalletToken[]>;
    createAuthToken(input: CreateAuthTokenInput): Promise<WorkerAuthToken>;
    /** By the token's code (`AUTH-…`), or by its ref (64 hex) when the code was not kept. */
    revokeAuthToken(walletKey: string, authTokenOrRef: string): Promise<void>;
    /** The wallet's v2 ref; null when the node names no ledger. */
    getWalletRef(walletKey: string): Promise<string | null>;
    /** Whether the ledger holds the wallet: on v2 a wallet exists only from its first credit. */
    exists(walletKey: string): Promise<boolean>;
    listReceiveAddresses(walletKey: string): Promise<string[]>;
    /** The refs of every active receive address the ledger lists for the wallet, codes kept here or not. */
    listAddressRefs(walletKey: string): Promise<string[]>;
    /** MINTS. The first address on a wallet is free, later ones are not. */
    createReceiveAddress(walletKey: string): Promise<{ receiveAddr: string }>;
    /** The balance in BIT, read with the VIEW code and confirmed by the latest signed row (D59). Throws otherwise. */
    balance(walletKey: string): Promise<string>;
}

/** One wallet as the pane renders it, after the Worker has been asked. */
export interface WalletView {
    slot: WalletSlot;
    walletRef: string | null;
    /** BIT, decimal text; null when it could not be read or verified. */
    balance: string | null;
    /** True when the ledger holds no wallet under this key yet: it is created by its first credit. */
    missing: boolean;
    /** The wallet's first receive address whose code this account keeps, or null. */
    receiveAddress: string | null;
    /** True when the ledger lists an active receive address whose code this account does not
     *  keep (the organization founder's, kept in the founder's own store): refs only here, and
     *  nothing to create — the wallet has its address. */
    receiveAddressHeldElsewhere: boolean;
    tokens: WalletToken[];
    /** Human-readable summary of whatever could NOT be read. */
    error?: string;
}

/**
 * Read one wallet. Every section degrades on its own: a Worker that answers
 * tokens but not balances still produces a usable pane, and the member is told
 * which half is missing rather than shown an empty group.
 *
 * Reads only — see the module header.
 */
export async function loadWalletView(
    worker: WalletsWorker, slot: WalletSlot,
): Promise<WalletView> {
    const problems: string[] = [];

    let walletRef: string | null = null;
    try {
        walletRef = await worker.getWalletRef(slot.key);
    } catch { /* cosmetic */ }

    let exists = true;
    try {
        exists = await worker.exists(slot.key);
    } catch {
        problems.push('the ledger could not be asked about this wallet');
    }
    if (!exists) return { slot, walletRef, balance: null, missing: true, receiveAddress: null, receiveAddressHeldElsewhere: false, tokens: [] };

    let balance: string | null = null;
    try {
        balance = await worker.balance(slot.key);
    } catch {
        problems.push('the balance could not be read or did not verify');
    }

    let receiveAddress: string | null = null;
    let receiveAddressHeldElsewhere = false;
    try {
        const addresses = await worker.listReceiveAddresses(slot.key);
        receiveAddress = addresses.find(a => verifyReceiveAddress(a)) ?? null;
        if (receiveAddress === null) receiveAddressHeldElsewhere = (await worker.listAddressRefs(slot.key)).length > 0;
    } catch {
        problems.push('the receive address could not be read');
    }

    let tokens: WalletToken[] = [];
    try {
        tokens = await worker.listAuthTokens(slot.key);
    } catch {
        problems.push('the spending tokens could not be read');
    }

    return {
        slot, walletRef, balance, missing: false, receiveAddress, receiveAddressHeldElsewhere, tokens,
        ...(problems.length ? { error: `This wallet is only partly readable: ${problems.join('; ')}.` } : {}),
    };
}

/**
 * The wallet's receive address, minting the FIRST one when it has none. Called
 * from a member's click: on a wallet that already carries an address this is a
 * read, and on one that does not it spends nothing (the first is free), but the
 * call is still deliberate because an automatic mint on a wallet whose address
 * list merely failed to load would burn a fee for nothing.
 */
/** Where a wallet card is drawn: an account's own wallet, or the organization's wallet
 *  as one of its owner or treasurer delegates reaches it through the org key wraps. */
export type WalletSurface = 'account' | 'organization';

/**
 * Whose permission a token is, as the card names it. On the ORGANIZATION wallet each
 * entitled delegate keeps its own codes (apps/bitcash/docs/plans/bitcash-v2.md M12, D4 ruling): the
 * default it issued is its own, and a token whose code this delegate does not keep
 * is another delegate's (or another device's) — listed by its ref, revocable,
 * never handed out.
 */
export function tokenOwnerText(token: WorkerAuthToken, surface: WalletSurface): string {
    const isDefault = token.label === DEFAULT_TOKEN_LABEL;
    if (surface === 'organization') {
        if (isDefault) return `your own default permission on this organization wallet (${DEFAULT_TOKEN_LABEL})`;
        if (!token.authToken) {
            return `another delegate's or another device's permission${token.ref ? ` (ref ${token.ref.slice(0, 12)}…)` : ''}; `
                + 'its code is not kept here, so it can be revoked but not handed to a service';
        }
        return token.label ?? 'a permission of yours';
    }
    if (isDefault) return `${DEFAULT_TOKEN_LABEL} (default)`;
    return token.label ?? 'kept on no device of yours';
}

/** What the Settings list says a member's default permission pays (§9.3): it names no payee. */
export const DEFAULT_TOKEN_PAYS = 'Catalog services, at any operator';

/**
 * What a token pays, as the Settings list names it. An UNBOUND token — the default one since
 * apps/docs/plans/second-node-and-bitcash-open-issues.md §9.3 — pays Catalog services at any
 * operator, within its service types. A bound one pays the receive address the account recorded;
 * the ledger names only its ref, so one this account never recorded is said to be bound, never
 * passed off as unbound. Whether it is bound is the ledger's word (`payeeRef`) when it gave one.
 */
export function tokenPayeeText(token: WorkerAuthToken): string {
    const bound = token.payeeRef !== undefined ? token.payeeRef !== null : (typeof token.payee === 'string' && token.payee !== '');
    if (!bound) return `pays ${DEFAULT_TOKEN_PAYS}`;
    if (typeof token.payee === 'string' && token.payee !== '') return `pays ${token.payee} only`;
    return 'bound to one receive address, which this account did not record';
}

/**
 * The confirmation for issuing (or reissuing) the default permission. On the
 * organization wallet it is THIS delegate's own token: only the wallet's first token
 * is free, so it costs a fee, and it takes one of the wallet's 128 permission slots
 * (revoked ones included) — every entitled delegate that needs one issues its own.
 */
export function defaultPermissionPrompt(args: { surface: WalletSurface; hasDefault: boolean; caps: TokenCaps }): { title: string; message: string } {
    const n = (v: number) => v.toLocaleString('en-US');
    const scope = `${n(args.caps.dailyCap)} BIT a day and ${n(args.caps.monthlyCap)} a month, for ${DEFAULT_TOKEN_PAYS}`;
    if (args.surface === 'organization') {
        return {
            title: args.hasDefault ? 'Reissue your own default permission' : 'Issue your own default permission',
            message: (args.hasDefault
                ? 'Your default permission on the organization wallet is revoked and a fresh one issued in its place. '
                : 'Issues YOUR OWN default permission on the organization wallet — the one services ask for when they '
                  + 'need the organization to pay for your work. Other delegates keep theirs; ')
                + `It is capped at ${scope}. Only the wallet's first permission is free, so this costs a fee, `
                + 'and it takes one of the wallet\'s 128 permission slots (revoked ones count).',
        };
    }
    return {
        title: args.hasDefault ? 'Reissue the default permission' : 'Issue the default permission',
        message: args.hasDefault
            ? `The permission this account's services hold is revoked and a fresh one is issued in its place (${scope}). `
              + 'Every service that holds the old one will ask you to approve the new one the next time it needs to pay.'
            : `Issues the permission this account's services ask for when they need to pay, capped at ${scope}.`,
    };
}

export async function ensureReceiveAddress(
    worker: WalletsWorker, walletKey: string,
): Promise<string> {
    const existing = (await worker.listReceiveAddresses(walletKey)).find(a => verifyReceiveAddress(a));
    if (existing) return existing;
    const created = await worker.createReceiveAddress(walletKey);
    if (!created.receiveAddr || !verifyReceiveAddress(created.receiveAddr)) {
        throw new Error('The Worker did not return a usable receive address');
    }
    return created.receiveAddr;
}

// ── the default token ───────────────────────────────────────────────────────

export interface TokenCaps {
    dailyCap: number;
    monthlyCap: number;
}

/** A token that is neither revoked nor past its expiry. */
export function isLiveToken(token: WorkerAuthToken, now: number = Date.now()): boolean {
    if (token.revokedAt) return false;
    return token.expiresAt === null || token.expiresAt === undefined || token.expiresAt > now;
}

/** The wallet's live `catalog:default` token, if it has one. */
export function findDefaultToken(
    tokens: readonly WorkerAuthToken[], now: number = Date.now(),
): WorkerAuthToken | null {
    return tokens.find(t => t.label === DEFAULT_TOKEN_LABEL && isLiveToken(t, now)) ?? null;
}

/** Never throws: a cache that cannot be cleared is a cache that will miss, and
 *  a revoke must not fail over its own side-effect (§4.12). */
async function dropSlot(cache: DefaultTokenCache | null | undefined, slot: string | null): Promise<void> {
    if (!cache || !slot) return;
    try { await cache.clear(slot); } catch { /* a miss is the safe direction */ }
}

async function fillSlot(
    cache: DefaultTokenCache | null | undefined, slot: string | null,
    token: WorkerAuthToken, walletRef: string | null, serviceTypes: readonly string[],
): Promise<void> {
    if (!cache || !slot || !token.authToken) return;
    try {
        await cache.set(slot, encodeCachedToken({
            token: token.authToken,
            ...(walletRef ? { walletRef } : {}),
            label: token.label ?? DEFAULT_TOKEN_LABEL,
            expiresAt: token.expiresAt ?? null,
            // the scope it was issued with (§9.3): what the handler serves it against
            serviceTypes: [...serviceTypes],
            unbound: true,
        }));
    } catch { /* the next grant refills */ }
}

export interface ReissueInput {
    worker: WalletsWorker;
    /** The wallet's spending key — already obtained through the supersecret
     *  prompt (account) or the org key wraps (organization). */
    walletKey: string;
    caps: TokenCaps;
    /** The node's `Voucher:DefaultToken:ServiceTypes`: the token is minted unbound with these (§9.3). */
    serviceTypes: readonly string[];
    /** `bitcash-token-cache`'s slot name, or null when this wallet has none
     *  (a second account wallet the member picked deliberately). */
    cacheSlot: string | null;
    cache: DefaultTokenCache | null;
    now?: () => number;
}

/**
 * Reissue the default token (§4.12): revoke the live one, mint its replacement
 * with the node's fixed scope — unbound, the configured service types, the D27
 * caps (§9.3) — and leave the device cache holding the NEW token.
 *
 * The order is the whole point. The cache is dropped BEFORE the Worker is
 * asked for anything, so every failure path — a revoke that half-lands, a mint
 * that never answers — ends with an empty slot rather than a slot holding a
 * token the Worker has already revoked. An empty slot costs the next module
 * request one refill; a stale slot costs it a 403 and a second prompt.
 */
export async function reissueDefaultToken(input: ReissueInput): Promise<WorkerAuthToken> {
    const now = input.now ?? (() => Date.now());
    requireServiceTypes(input.serviceTypes);
    await dropSlot(input.cache, input.cacheSlot);

    const existing = (await input.worker.listAuthTokens(input.walletKey))
        .filter(t => t.label === DEFAULT_TOKEN_LABEL && isLiveToken(t, now()));
    for (const token of existing) {
        // by its code, or by its ref when no device of this account kept the code
        const which = token.authToken || token.ref;
        if (which) await input.worker.revokeAuthToken(input.walletKey, which);
    }

    // The fixed scope of D6/D27 as amended (§9.3): payee `operator` — no payee,
    // so ONE token serves Catalog services at every operator — the node's service
    // types (what keeps it from paying content), no site, no expiry, the
    // configured caps.
    const minted = await input.worker.createAuthToken({
        walletKey: input.walletKey,
        label: DEFAULT_TOKEN_LABEL,
        payee: 'operator',
        sites: [],
        serviceTypes: [...input.serviceTypes],
        expiresAt: null,
        dailyCap: input.caps.dailyCap,
        monthlyCap: input.caps.monthlyCap,
    });

    let walletRef: string | null = null;
    try { walletRef = await input.worker.getWalletRef(input.walletKey); } catch { /* cosmetic */ }
    await fillSlot(input.cache, input.cacheSlot, minted, walletRef, input.serviceTypes);
    return minted;
}

export interface RevokeTokenInput {
    worker: WalletsWorker;
    walletKey: string;
    token: WorkerAuthToken;
    cacheSlot: string | null;
    cache: DefaultTokenCache | null;
}

/**
 * Revoke one token. When it was the default one, the device cache goes with it
 * — §4.12's "the member revoking in Settings clears the cache". Anything else
 * leaves the cache alone: a module's own `catalog:<moduleId>` token was never
 * in it.
 */
export async function revokeWalletToken(input: RevokeTokenInput): Promise<void> {
    // The ledger revokes by the code or by the ref: a token whose code this account
    // never kept (another device's, another delegate's) is revoked by its ref.
    const which = input.token.authToken || input.token.ref;
    if (!which) throw new Error('That token has no identifier to revoke');
    await input.worker.revokeAuthToken(input.walletKey, which);
    if (input.token.label === DEFAULT_TOKEN_LABEL) {
        await dropSlot(input.cache, input.cacheSlot);
    }
}

export interface MintDelegateTokenInput {
    worker: WalletsWorker;
    /** The ORGANIZATION wallet's key, through the org wraps (§4.13). */
    walletKey: string;
    delegateCid: string;
    caps: TokenCaps;
    /** The node's `Voucher:DefaultToken:ServiceTypes`: the token is unbound, so it is scoped to these (§9.3). */
    serviceTypes: readonly string[];
}

/**
 * A deliberate per-delegate token on the organization's wallet (§4.13). Never
 * minted by default: each one takes a slot out of the wallet's 32 active / 128
 * permanent extensions, which is why this is an explicit action with its own
 * caps rather than something the consent dialog does on the member's behalf.
 */
export async function mintDelegateToken(input: MintDelegateTokenInput): Promise<WorkerAuthToken> {
    const delegateCid = input.delegateCid.trim();
    if (!delegateCid) throw new Error('Choose the delegate this token is for');
    requireCaps(input.caps);
    requireServiceTypes(input.serviceTypes);
    return await input.worker.createAuthToken({
        walletKey: input.walletKey,
        label: delegateTokenLabel(delegateCid),
        payee: 'operator',
        sites: [],
        serviceTypes: [...input.serviceTypes],
        expiresAt: null,
        dailyCap: input.caps.dailyCap,
        monthlyCap: input.caps.monthlyCap,
    });
}

/** An unbound token without service types would pay content, at any site (§9.3 D-a): never minted. */
function requireServiceTypes(types: readonly string[]): void {
    if (types.length === 0) {
        throw new Error('This Catalog.ID server names no service types for the default permission, so none can be issued.');
    }
}

export function requireCaps(caps: TokenCaps): TokenCaps {
    for (const [name, value] of [['daily', caps.dailyCap], ['monthly', caps.monthlyCap]] as const) {
        if (!Number.isInteger(value) || value <= 0) {
            throw new Error(`The ${name} cap must be a whole number of bits above zero`);
        }
    }
    if (caps.monthlyCap < caps.dailyCap) {
        throw new Error('The monthly cap cannot be lower than the daily cap');
    }
    return caps;
}

// ── publishing the receive address (§4.11) ──────────────────────────────────

/**
 * The publication path, as a dependency set. The same function serves the
 * Settings action and the print step's *Publish* checkbox (§7), so neither can
 * publish something the other would not.
 */
export interface PublishReceiveAddressDeps {
    encryptClaim(value: string, ownerPublicKey: Uint8Array, label?: string): Promise<{
        encryptedValue: string; nonce: string; encryptedKey: string;
        encryptedLabel?: string; labelNonce?: string;
    }>;
    createClaim(req: CreateClaimRequest): Promise<{ claimUuid: string }>;
    listOperators(): Promise<OperatorInfo[]>;
    reEncryptClaimKey(
        encryptedKey: string, sharerSecretKey: Uint8Array, recipientPublicKey: Uint8Array,
    ): Promise<string>;
    publishClaim(claimUuid: string, req: { operatorKeys: { operatorCid: string; encryptedKey: string }[] }): Promise<void>;
    /** Base64 decoder for the operators' public keys. */
    fromBase64?(value: string): Uint8Array;
    /** The publishing account's `attr_encrypt` key pair. */
    ownerPublicKey: Uint8Array;
    ownerSecretKey: Uint8Array;
}

export interface PublishReceiveAddressResult {
    claimUuid: string;
    /** Always `pending`: publication goes through operator review (§4.11 — the
     *  ordinary publication path). */
    status: 'pending';
}

/**
 * Create and publish the `bitcash_receive_address` claim for one wallet.
 *
 * The checksum is verified BEFORE anything is created (§4.11), and it must be a
 * FUND address specifically: a CASH key pasted here would publish the member's
 * SPENDING KEY to the world, which is the one mistake this whole lane must make
 * impossible. `verifyReceiveAddress` refuses every other type by construction.
 */
export async function publishReceiveAddressClaim(
    deps: PublishReceiveAddressDeps,
    input: { address: string; label?: string },
): Promise<PublishReceiveAddressResult> {
    const address = input.address.trim();
    if (!verifyReceiveAddress(address)) {
        throw new Error('That is not a valid bitcash receive address — nothing was published');
    }

    const operators = await deps.listOperators();
    if (operators.length === 0) {
        throw new Error('No operator is available to review the publication. '
            + 'Try again once your node has an operator binding.');
    }

    const encrypted = await deps.encryptClaim(
        address, deps.ownerPublicKey, input.label ?? 'Bitcash receive address');
    const created = await deps.createClaim({
        claimType: RECEIVE_ADDRESS_CLAIM_TYPE,
        encryptedValue: encrypted.encryptedValue,
        nonce: encrypted.nonce,
        encryptedKey: encrypted.encryptedKey,
        ...(encrypted.encryptedLabel ? { encryptedLabel: encrypted.encryptedLabel } : {}),
        ...(encrypted.labelNonce ? { labelNonce: encrypted.labelNonce } : {}),
    });

    const decode = deps.fromBase64 ?? defaultFromBase64;
    const operatorKeys: { operatorCid: string; encryptedKey: string }[] = [];
    for (const op of operators) {
        operatorKeys.push({
            operatorCid: op.cid,
            encryptedKey: await deps.reEncryptClaimKey(
                encrypted.encryptedKey, deps.ownerSecretKey, decode(op.publicKey)),
        });
    }
    await deps.publishClaim(created.claimUuid, { operatorKeys });
    return { claimUuid: created.claimUuid, status: 'pending' };
}

function defaultFromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
}

/**
 * The publication this address already has, or null. Matched on the claim TYPE
 * and the public value together: a member with two wallets has two addresses,
 * and showing the other one's review state beside this one would be a lie.
 */
export function findReceiveAddressPublication(
    publicClaims: readonly PublicClaimSummary[], address: string,
): PublicClaimSummary | null {
    const wanted = address.trim().toUpperCase();
    return publicClaims.find(pc =>
        pc.claimType === RECEIVE_ADDRESS_CLAIM_TYPE
        && (pc.publicValue ?? '').trim().toUpperCase() === wanted) ?? null;
}

// The wallet mutations (rename, import, mint) moved to
// `crypto/bitcash-wallets-doc.ts` when personal wallets left the key bag
// (cid-encrypted-storage §7.2): they plan a DOCUMENT record now, and
// `state/bitcash-wallets-store.ts` lands them with compare-and-set.
